/**
 * RedirectUrlSection Component
 *
 * Main redirect URL configuration section.
 * Located under description, expanded by default.
 *
 * Features:
 * - Fallback URL input (base redirect URL with {{placeholder}} support)
 * - [+ Add Condition] button for conditional redirects
 * - Conditional redirect rules (lazy-loaded after first condition added)
 *
 * @module webplacements/components/editors
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Button,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type {
  ParameterDefinition,
  WebConditionalRedirectConfig,
  WebRedirectRule,
  RedirectCondition,
  RedirectOperator,
} from '@modules/webplacements/types/webplacements.types';

export interface RedirectUrlSectionProps {
  /** Fallback/base redirect URL template */
  fallbackUrl: string | undefined;
  /** URL encode toggle */
  urlEncode: boolean;
  /** Conditional redirect configuration */
  conditionalRedirect: WebConditionalRedirectConfig | undefined;
  /** All defined parameters (for placeholder preview and conditions) */
  availableParameters: ParameterDefinition[];
  /** Change handlers */
  onFallbackUrlChange: (url: string | undefined) => void;
  onUrlEncodeChange: (encode: boolean) => void;
  onConditionalRedirectChange: (config: WebConditionalRedirectConfig | undefined) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

const OPERATOR_LABELS: Record<RedirectOperator, string> = {
  eq: 'equals',
  neq: 'not equals',
  in: 'is in list',
  notIn: 'not in list',
  contains: 'contains',
  regex: 'matches regex',
};

/**
 * Generate a unique rule ID
 */
const generateRuleId = (): string => `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * RedirectUrlSection Component
 *
 * Provides the main redirect URL configuration with optional conditional rules.
 */
export const RedirectUrlSection: React.FC<RedirectUrlSectionProps> = ({
  fallbackUrl,
  urlEncode,
  conditionalRedirect,
  availableParameters,
  onFallbackUrlChange,
  onUrlEncodeChange,
  onConditionalRedirectChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const [showConditions, setShowConditions] = useState(
    (conditionalRedirect?.rules?.length ?? 0) > 0
  );

  // Extract placeholders from URL
  const usedPlaceholders = useMemo(() => {
    if (!fallbackUrl) return [];
    const matches = fallbackUrl.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return matches.map((m) => m.replace(/\{\{|\}\}/g, ''));
  }, [fallbackUrl]);

  // Find undefined placeholders
  const undefinedPlaceholders = useMemo(() => {
    const definedKeys = new Set(availableParameters.map((p) => p.key));
    return usedPlaceholders.filter((p) => !definedKeys.has(p));
  }, [usedPlaceholders, availableParameters]);

  const handleAddCondition = useCallback(() => {
    setShowConditions(true);

    const newRule: WebRedirectRule = {
      id: generateRuleId(),
      name: 'New Condition',
      priority: 100,
      isActive: true,
      conditions: [],
      targetUrl: '',
    };

    const currentRules = conditionalRedirect?.rules ?? [];
    onConditionalRedirectChange({
      rules: [...currentRules, newRule],
    });
  }, [conditionalRedirect, onConditionalRedirectChange]);

  const handleRuleChange = useCallback(
    (ruleId: string, updates: Partial<WebRedirectRule>) => {
      const currentRules = conditionalRedirect?.rules ?? [];
      const updatedRules = currentRules.map((rule) =>
        rule.id === ruleId ? { ...rule, ...updates } : rule
      );
      onConditionalRedirectChange({ rules: updatedRules });
    },
    [conditionalRedirect, onConditionalRedirectChange]
  );

  const handleDeleteRule = useCallback(
    (ruleId: string) => {
      const currentRules = conditionalRedirect?.rules ?? [];
      const updatedRules = currentRules.filter((rule) => rule.id !== ruleId);
      onConditionalRedirectChange(
        updatedRules.length > 0 ? { rules: updatedRules } : undefined
      );
      if (updatedRules.length === 0) {
        setShowConditions(false);
      }
    },
    [conditionalRedirect, onConditionalRedirectChange]
  );

  const handleAddConditionToRule = useCallback(
    (ruleId: string) => {
      const currentRules = conditionalRedirect?.rules ?? [];
      const updatedRules = currentRules.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const newCondition: RedirectCondition = {
          field: availableParameters[0]?.key || '',
          operator: 'eq',
          value: '',
        };
        return {
          ...rule,
          conditions: [...rule.conditions, newCondition],
        };
      });
      onConditionalRedirectChange({ rules: updatedRules });
    },
    [conditionalRedirect, availableParameters, onConditionalRedirectChange]
  );

  const handleConditionChange = useCallback(
    (ruleId: string, conditionIndex: number, updates: Partial<RedirectCondition>) => {
      const currentRules = conditionalRedirect?.rules ?? [];
      const updatedRules = currentRules.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const updatedConditions = rule.conditions.map((cond, idx) =>
          idx === conditionIndex ? { ...cond, ...updates } : cond
        );
        return { ...rule, conditions: updatedConditions };
      });
      onConditionalRedirectChange({ rules: updatedRules });
    },
    [conditionalRedirect, onConditionalRedirectChange]
  );

  const handleDeleteCondition = useCallback(
    (ruleId: string, conditionIndex: number) => {
      const currentRules = conditionalRedirect?.rules ?? [];
      const updatedRules = currentRules.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const updatedConditions = rule.conditions.filter((_, idx) => idx !== conditionIndex);
        return { ...rule, conditions: updatedConditions };
      });
      onConditionalRedirectChange({ rules: updatedRules });
    },
    [conditionalRedirect, onConditionalRedirectChange]
  );

  const rules = conditionalRedirect?.rules ?? [];

  return (
    <Box>
      {/* Fallback URL Input */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Redirect URL
          </Typography>
          <Tooltip title="Where users are redirected after completing the funnel. Use {{parameter}} placeholders to include collected data.">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>

        <TextField
          label="Redirect URL"
          value={fallbackUrl || ''}
          onChange={(e) => onFallbackUrlChange(e.target.value || undefined)}
          disabled={disabled}
          fullWidth
          size="small"
          placeholder="https://example.com/thank-you?email={{email}}&source={{source}}"
          helperText="Use {{parameter}} syntax to insert collected data"
          InputProps={{
            sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
          }}
        />
      </Box>

      {/* Placeholders Preview */}
      {usedPlaceholders.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Placeholders:
          </Typography>
          {usedPlaceholders.map((placeholder) => {
            const isDefined = availableParameters.some((p) => p.key === placeholder);
            return (
              <Chip
                key={placeholder}
                label={placeholder}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: isDefined
                    ? theme.palette.success.main + '20'
                    : theme.palette.error.main + '20',
                  color: isDefined ? theme.palette.success.dark : theme.palette.error.dark,
                  borderColor: isDefined ? theme.palette.success.main : theme.palette.error.main,
                }}
                variant="outlined"
              />
            );
          })}
        </Box>
      )}

      {/* Undefined Placeholders Warning */}
      {undefinedPlaceholders.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: theme.palette.warning.main + '15',
            borderRadius: 1,
            border: `1px solid ${theme.palette.warning.main}40`,
          }}
        >
          <Typography variant="caption" color="warning.dark">
            <strong>Warning:</strong> Some placeholders are not defined:{' '}
            {undefinedPlaceholders.map((p) => `{{${p}}}`).join(', ')}.
            Define them in the parameter sections above.
          </Typography>
        </Box>
      )}

      {/* Add Condition Button */}
      {!showConditions && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
          disabled={disabled}
          variant="outlined"
          color="secondary"
        >
          Add Conditional Redirect
        </Button>
      )}

      {/* Conditional Redirect Rules */}
      <Collapse in={showConditions}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Conditional Redirects
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Rules are evaluated in priority order. First matching rule wins, otherwise fallback URL
            is used.
          </Typography>

          {rules.map((rule, ruleIndex) => (
            <Paper
              key={rule.id}
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              {/* Rule Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DragIndicatorIcon fontSize="small" color="action" />
                <TextField
                  value={rule.name}
                  onChange={(e) => handleRuleChange(rule.id, { name: e.target.value })}
                  size="small"
                  variant="standard"
                  placeholder="Rule name"
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { fontWeight: 600 } }}
                />
                <TextField
                  label="Priority"
                  type="number"
                  value={rule.priority}
                  onChange={(e) =>
                    handleRuleChange(rule.id, { priority: parseInt(e.target.value) || 0 })
                  }
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ min: 0, max: 1000 }}
                />
                <Checkbox
                  checked={rule.isActive}
                  onChange={(e) => handleRuleChange(rule.id, { isActive: e.target.checked })}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRule(rule.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Conditions */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                When ALL conditions match:
              </Typography>

              {rule.conditions.map((condition, condIndex) => (
                <Box
                  key={condIndex}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mb: 1,
                    pl: 2,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Parameter</InputLabel>
                    <Select
                      value={condition.field}
                      onChange={(e) =>
                        handleConditionChange(rule.id, condIndex, { field: e.target.value })
                      }
                      label="Parameter"
                    >
                      {availableParameters.map((param) => (
                        <MenuItem key={param.key} value={param.key}>
                          {param.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator}
                      onChange={(e) =>
                        handleConditionChange(rule.id, condIndex, {
                          operator: e.target.value as RedirectOperator,
                        })
                      }
                      label="Operator"
                    >
                      {Object.entries(OPERATOR_LABELS).map(([op, label]) => (
                        <MenuItem key={op} value={op}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    value={
                      Array.isArray(condition.value)
                        ? condition.value.join(', ')
                        : condition.value
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      const newValue =
                        condition.operator === 'in' || condition.operator === 'notIn'
                          ? val.split(',').map((v) => v.trim())
                          : val;
                      handleConditionChange(rule.id, condIndex, { value: newValue });
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder={
                      condition.operator === 'in' || condition.operator === 'notIn'
                        ? 'value1, value2, value3'
                        : 'value'
                    }
                  />

                  <IconButton
                    size="small"
                    onClick={() => handleDeleteCondition(rule.id, condIndex)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddConditionToRule(rule.id)}
                sx={{ ml: 2, mb: 2 }}
              >
                Add Condition
              </Button>

              {/* Target URL */}
              <TextField
                label="Target URL (when conditions match)"
                value={rule.targetUrl}
                onChange={(e) => handleRuleChange(rule.id, { targetUrl: e.target.value })}
                size="small"
                fullWidth
                placeholder="https://example.com/special?param={{value}}"
                helperText="Supports {{placeholder}} syntax"
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
            </Paper>
          ))}

          {/* Add Another Rule */}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
            disabled={disabled}
          >
            Add Another Condition
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RedirectUrlSection;
